import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { generateSubTaskId } from '../utils/idGenerator';
import ItemCardSelectionModal from './ItemCardSelectionModal';
import { ItemCardService } from '../services/itemCardService';
import { ItemCard } from '../types/itemCard';
import ConfirmModal from './ConfirmModal';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ProjectAssignment, SubTask } from '../types';
import { ClassMember, User } from '../types/alarm';
import { projectAssignmentService } from '../services/projectAssignmentService';
import { userDataService } from '../services/userDataService';
import { checkAssignmentTypeLimit, isStandardPlanOrAbove } from '../utils/licenseLimits';
import LicenseLimitModal from './LicenseLimitModal';
import { generateAssignmentId } from '../utils/idGenerator';
import { getOrderedMemberGroups } from '../utils/memberGroupOrder';

// 드래그 가능한 하위 과제 행 컴포넌트
const SortableSubTaskRow: React.FC<{
  subTask: SubTask;
  updateSubTask: (id: string, field: keyof SubTask, value: string | number) => void;
  removeSubTask: (id: string) => void;
  order: number;
}> = ({ subTask, updateSubTask, removeSubTask, order }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: subTask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <SubTaskRow ref={setNodeRef} style={style}>
      <SubTaskCell>
        <span style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--color-text-gray)' }}>
          {order}
        </span>
      </SubTaskCell>
      <SubTaskCell>
        <SubTaskInput
          type="text"
          value={subTask.title}
          onChange={(e) => updateSubTask(subTask.id, 'title', e.target.value)}
          placeholder="하위 과제 제목"
          style={{ borderRight: 'none' }}
        />
      </SubTaskCell>
      <SubTaskCell>
        <SubTaskInput
          type="text"
          inputMode="numeric"
          value={subTask.points === 0 ? '' : subTask.points}
          onFocus={(e) => {
            // 포커스 시 전체 선택하여 새로 입력하면 기존 값이 지워지도록
            e.target.select();
          }}
          onChange={(e) => {
            const value = e.target.value;
            // 빈 문자열이면 0으로 설정
            if (value === '') {
              updateSubTask(subTask.id, 'points', 0);
            } else {
              // 숫자만 허용
              const numValue = parseInt(value.replace(/[^0-9]/g, ''));
              if (!isNaN(numValue)) {
                updateSubTask(subTask.id, 'points', numValue);
              }
            }
          }}
          onKeyDown={(e) => {
            // 백스페이스로 모든 숫자를 지울 수 있도록 허용
            if (e.key === 'Backspace' && e.currentTarget.value === '0') {
              e.preventDefault();
              updateSubTask(subTask.id, 'points', 0);
            }
          }}
          min="0"
          style={{ borderRight: 'none' }}
        />
      </SubTaskCell>
      <SubTaskCell>
        <DeleteSubTaskButton onClick={() => removeSubTask(subTask.id)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
          </svg>
        </DeleteSubTaskButton>
      </SubTaskCell>
      <SubTaskCell>
        <DragHandle {...attributes} {...listeners}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
          </svg>
        </DragHandle>
      </SubTaskCell>
    </SubTaskRow>
  );
};

interface ProjectAssignmentSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (projectAssignment: any) => void;
  members: ClassMember[];
  memberGroups?: string[];
  memberGroupOrder?: string[];
  currentMemberGroup?: string;
  memberGroupNames?: { [groupKey: string]: string };
  mode?: 'new' | 'copy' | 'edit';
  initialData?: Partial<ProjectAssignment>;
  initialSelectedIds?: string[];
  currentClassId: string;
  userId: string;
  user: User; // 사용자 정보 추가 (라이센스 체크용)
  editingProject?: ProjectAssignment | null;
  defaultPoints?: number; // 기본 포인트 단위
  onLicenseClick?: () => void; // 이용권 구매 모달 열기
}

const ProjectAssignmentSettingsModal: React.FC<ProjectAssignmentSettingsModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  members, 
  memberGroups,
  memberGroupOrder,
  currentMemberGroup = 'A',
  memberGroupNames,
  mode = 'new', 
  initialData, 
  initialSelectedIds,
  currentClassId,
  userId,
  user,
  editingProject,
  defaultPoints = 10,
  onLicenseClick
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [subTasks, setSubTasks] = useState<SubTask[]>([]);
  const normalizedGroups = useMemo(() => {
    let gs = getOrderedMemberGroups(memberGroups, memberGroupOrder);

    if (!isStandardPlanOrAbove(user.userType)) {
      gs = ['A'];
    }

    return gs.length > 0 ? gs : ['A'];
  }, [memberGroups, memberGroupOrder, user.userType]);

  const [activeGroup, setActiveGroup] = useState<string>('A');
  const [membersByGroup, setMembersByGroup] = useState<Record<string, ClassMember[]>>({});
  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, Set<string>>>({});
  const [selectionLoadedGroups, setSelectionLoadedGroups] = useState<Set<string>>(new Set());

  const currentGroupMembers = membersByGroup[activeGroup] || members;
  const selected = selectedByGroup[activeGroup] || new Set<string>();
  const setSelected = (next: Set<string>) => {
    setSelectedByGroup(prev => ({ ...prev, [activeGroup]: next }));
  };
  const [hoveredStudent, setHoveredStudent] = useState<{id: string, name: string} | null>(null);
  const [selectedColor, setSelectedColor] = useState('#FC5555');
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  
  // 프로젝트 보상 관련 상태
  const [rewardEnabled, setRewardEnabled] = useState(false);
  const [rewardMessage, setRewardMessage] = useState('');
  const [rewardCoinsDisplay, setRewardCoinsDisplay] = useState<string>('');
  const getRewardCoinsNum = () => (rewardCoinsDisplay === '' ? 0 : (Number(rewardCoinsDisplay) || 0));
  const [rewardItemCardIds, setRewardItemCardIds] = useState<string[]>([]);
  
  const [isItemCardModalOpen, setIsItemCardModalOpen] = useState(false);
  const [selectedItemCards, setSelectedItemCards] = useState<ItemCard[]>([]);
  const [rewardCardImageErrors, setRewardCardImageErrors] = useState<Set<string>>(new Set());
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const getGroupDisplayName = useMemo(() => {
    return (groupKey: string) => memberGroupNames?.[groupKey] || `${groupKey}그룹`;
  }, [memberGroupNames]);

  // 초기 상태 저장용
  const [initialState, setInitialState] = useState({
    title: '',
    description: '',
    dueDate: '',
    subTasks: [] as SubTask[],
    rewardEnabled: false,
    rewardMessage: '',
    rewardCoins: 0,
    rewardItemCardIds: [] as string[],
    assignedStudentIds: [] as string[]
  });

  // 초기 상태 저장 여부 추적
  const initialStateSavedRef = useRef(false);

  // 변경사항 확인 함수
  const hasUnsavedChanges = useMemo(() => {
    // 초기 로딩이 완료되지 않은 경우 변경사항 없음으로 처리
    if (!isInitialLoadComplete) return false;

    // 기본 정보 변경 체크
    const hasBasicChanges =
      title !== initialState.title ||
      description !== initialState.description ||
      dueDate !== initialState.dueDate;

    // 하위 과제 변경 체크
    const hasSubTaskChanges =
      subTasks.length !== initialState.subTasks.length ||
      subTasks.some((subTask, index) => {
        const initialSubTask = initialState.subTasks[index];
        return !initialSubTask ||
          subTask.title !== initialSubTask.title ||
          subTask.points !== initialSubTask.points;
      });

    // 학생 선택 변경 체크
    const currentSelectedIds = Object.values(selectedByGroup).flatMap(set => Array.from(set));
    const hasSelectionChanges =
      currentSelectedIds.length !== initialState.assignedStudentIds.length ||
      !currentSelectedIds.every(id => initialState.assignedStudentIds.includes(id)) ||
      !initialState.assignedStudentIds.every(id => currentSelectedIds.includes(id));

    // 보상 설정 변경 체크
    const hasRewardChanges =
      rewardEnabled !== initialState.rewardEnabled ||
      rewardMessage !== initialState.rewardMessage ||
      getRewardCoinsNum() !== initialState.rewardCoins ||
      JSON.stringify(rewardItemCardIds.sort()) !== JSON.stringify(initialState.rewardItemCardIds.sort());

    return hasBasicChanges || hasSubTaskChanges || hasSelectionChanges || hasRewardChanges;
  }, [
    title, description, dueDate, subTasks,
    selectedByGroup, rewardEnabled, rewardMessage, rewardCoinsDisplay, rewardItemCardIds,
    initialState, isInitialLoadComplete
  ]);

  // 모달 닫기 핸들러 (변경사항 확인 포함)
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  // 아이템 카드 정보 로드 함수
  const loadSelectedItemCards = useCallback(async (itemCardIds: string[]) => {
    if (itemCardIds.length === 0) {
      setSelectedItemCards([]);
      setRewardCardImageErrors(new Set()); // 이미지 에러 초기화
      return;
    }

    try {
      const itemCardService = new ItemCardService(userId, currentClassId);
      const itemCardSet = await itemCardService.loadItemCardSet();
      
      if (itemCardSet) {
        const allItems = [...itemCardSet.levelupItem, ...itemCardSet.assignmentItem, ...itemCardSet.storeItem];
        const selectedItems = allItems.filter(item => itemCardIds.includes(item.itemCardId));
        setSelectedItemCards(selectedItems);
        setRewardCardImageErrors(new Set()); // 카드 정보 로드 시 이미지 에러 초기화
      }
    } catch (error) {
      console.error('아이템 카드 정보 로드 실패:', error);
      setSelectedItemCards([]);
      setRewardCardImageErrors(new Set());
    }
  }, [userId, currentClassId]);

  // 색상을 60% 밝기로 만드는 함수
  const lightenColor = (hex: string, brightness: number = 0.6): string => {
    // # 제거
    const cleanHex = hex.replace('#', '');
    
    // RGB 값 추출
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    
    // 밝기 조정: 원래 RGB * (1 - brightness) + 255 * brightness
    const newR = Math.round(r * (1 - brightness) + 255 * brightness);
    const newG = Math.round(g * (1 - brightness) + 255 * brightness);
    const newB = Math.round(b * (1 - brightness) + 255 * brightness);
    
    // 16진수로 변환
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // 레벨 색상 (레벨 1~10)
  const levelColors = [
    '#FC5555', // 레벨1
    '#FF9933', // 레벨2
    '#669900', // 레벨3
    '#119473', // 레벨4
    '#0066CC', // 레벨5
    '#9900CC', // 레벨6
    '#D60093', // 레벨7
    '#920049', // 레벨8
    '#CCCC00', // 레벨9
    '#565050'  // 레벨10
  ];

  // 프로젝트 색상 팔레트: 레벨 색상 + 각 색상의 60% 밝기 버전
  const projectColors = [
    ...levelColors, // 레벨 1~10 원본 색상
    ...levelColors.map(color => lightenColor(color, 0.6)) // 각 색상의 60% 밝기 버전
  ];

  const toggleAll = () => {
    if (selected.size === currentGroupMembers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(currentGroupMembers.map(m => m.id)));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // 오늘 날짜 기본값
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      const todayStr = `${yyyy}-${mm}-${dd}`;

      // 편집 모드일 때는 editingProject 정보 사용, 아니면 initialData 사용
      const dataToUse = editingProject || initialData;
      
      setTitle(dataToUse?.title ?? '');
      setDescription(dataToUse?.description ?? '');
      setSelectedColor(dataToUse?.color ?? '#FC5555');
      
      // 프로젝트 보상 관련 필드 초기화
      const newRewards = dataToUse?.rewardItemCardIds ?? [];
      setRewardEnabled(dataToUse?.rewardEnabled ?? false);
      setRewardMessage(dataToUse?.rewardMessage ?? '');
      setRewardCoinsDisplay(dataToUse?.rewardCoins != null ? String(dataToUse.rewardCoins) : '');
      setRewardItemCardIds(newRewards);
      
      // 완료 보상 아이템 카드 정보 로드
      if (newRewards.length > 0) {
        loadSelectedItemCards(newRewards);
      } else {
        setSelectedItemCards([]);
      }
      
      // 마감일 초기화: dataToUse에 dueDate가 있으면 사용, 없으면 오늘 날짜
      if (dataToUse?.dueDate) {
        const initDue = new Date(dataToUse.dueDate);
        setDueDate(isNaN(initDue.getTime()) ? todayStr : initDue.toISOString().slice(0,10));
      } else {
        setDueDate(todayStr);
      }
      
      // 하위과제 초기화: 편집 모드에서는 기존 ID 보존, 복제 모드에서는 새 ID 생성
      if (dataToUse?.subTasks && dataToUse.subTasks.length > 0) {
        const newSubTasks = dataToUse.subTasks.map((subTask, index) => ({
          ...subTask,
          id: mode === 'copy' ? generateSubTaskId() : subTask.id, // 복제 시에만 새 ID 생성
        }));
        setSubTasks(newSubTasks);
      } else {
        setSubTasks([]);
      }
      
      // 멤버 선택 상태 초기화 - dataToUse.assignedStudentIds 우선 사용
      let initialIds: string[];
      if (dataToUse?.assignedStudentIds && dataToUse.assignedStudentIds.length > 0) {
        initialIds = dataToUse.assignedStudentIds;
        console.log('🎯 initialData.assignedStudentIds 사용:', initialIds);
      } else if (initialSelectedIds && initialSelectedIds.length > 0) {
        initialIds = initialSelectedIds;
        console.log('🎯 initialSelectedIds 사용:', initialIds);
      } else {
        initialIds = members.map(m => m.id);
        console.log('🎯 모든 멤버 선택 (기본값):', initialIds);
      }
      
      console.log('🎯 모달 열림 - 초기 멤버 선택:', {
        initialSelectedIds,
        initialDataAssignedStudentIds: initialData?.assignedStudentIds,
        membersCount: members.length,
        initialIds,
        mode
      });
      
      const primaryGroup = (currentMemberGroup || 'A').toString().trim().toUpperCase();
      setActiveGroup(primaryGroup);
      setMembersByGroup({ [primaryGroup]: members });
      setSelectedByGroup({ [primaryGroup]: new Set(initialIds) });
      setSelectionLoadedGroups(new Set(mode === 'edit' ? [primaryGroup] : []));

      // 초기 로딩 완료 표시 (즉시)
      setIsInitialLoadComplete(true);
    } else {
      document.body.style.overflow = 'unset';
      // 모달이 닫힐 때 초기 로딩 상태 및 초기 상태 저장 플래그 리셋
      setIsInitialLoadComplete(false);
      setShowConfirmModal(false);
      initialStateSavedRef.current = false;
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialData, initialSelectedIds, members, mode, editingProject, currentMemberGroup, loadSelectedItemCards]);

  // 초기 상태 저장 (모달 열릴 때 딱 한 번만)
  useEffect(() => {
    if (!isOpen || !isInitialLoadComplete || initialStateSavedRef.current) return;

    // 현재 선택된 학생 IDs 계산
    const currentSelectedIds = Object.values(selectedByGroup).flatMap(set => Array.from(set));

    // 실제 상태 값들로 초기 상태 저장 (한 번만)
    setInitialState({
      title: title,
      description: description,
      dueDate: dueDate,
      subTasks: subTasks,
      rewardEnabled: rewardEnabled,
      rewardMessage: rewardMessage,
      rewardCoins: getRewardCoinsNum(),
      rewardItemCardIds: rewardItemCardIds,
      assignedStudentIds: currentSelectedIds
    });

    // 초기 상태 저장 완료 표시
    initialStateSavedRef.current = true;
  }, [
    isOpen,
    isInitialLoadComplete,
    title,
    description,
    dueDate,
    subTasks,
    rewardEnabled,
    rewardMessage,
    rewardCoinsDisplay,
    rewardItemCardIds,
    selectedByGroup
  ]);

  const ensureMembersLoaded = async (group: string) => {
    const g = (group || 'A').toString().trim().toUpperCase();
    if (membersByGroup[g]) return;
    try {
      const loaded = await userDataService.loadClassMembers(userId, currentClassId, g);
      setMembersByGroup(prev => ({ ...prev, [g]: loaded }));
      setSelectedByGroup(prev => ({ ...prev, [g]: prev[g] || new Set<string>() }));
    } catch (e) {
      console.error('프로젝트 과제 모달 - 멤버 그룹 로드 실패:', e);
      setMembersByGroup(prev => ({ ...prev, [g]: [] }));
      setSelectedByGroup(prev => ({ ...prev, [g]: prev[g] || new Set<string>() }));
    }
  };

  const ensureAssignmentSelectionLoaded = async (group: string) => {
    const g = (group || 'A').toString().trim().toUpperCase();
    if (mode !== 'edit') return;
    const assignmentId = (editingProject?.id || initialData?.id) as string | undefined;
    if (!assignmentId) return;
    if (selectionLoadedGroups.has(g)) return;

    try {
      const found = await projectAssignmentService.getProjectAssignment(userId, currentClassId, assignmentId, g);
      const ids = Array.isArray(found?.assignedStudentIds) ? found!.assignedStudentIds : [];
      setSelectedByGroup(prev => ({ ...prev, [g]: new Set(ids) }));
    } catch (e) {
      console.error('프로젝트 과제 모달 - 그룹별 선택 로드 실패:', e);
    } finally {
      setSelectionLoadedGroups(prev => {
        const next = new Set(prev);
        next.add(g);
        return next;
      });
    }
  };

  // 토글 함수
  const toggle = (memberId: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(memberId)) {
      newSelected.delete(memberId);
    } else {
      newSelected.add(memberId);
    }
    setSelected(newSelected);

    console.log('🎯 멤버 선택 토글:', {
      memberId,
      newSelected: Array.from(newSelected),
      memberName: members.find(m => m.id === memberId)?.name
    });
  };

  // 하위 과제 추가
  const addSubTask = () => {
    const newSubTask: SubTask = {
      id: generateSubTaskId(),
      title: '',
      points: defaultPoints,
      order: subTasks.length + 1
    };
    setSubTasks([...subTasks, newSubTask]);
  };

  // 하위 과제 수정
  const updateSubTask = (id: string, field: keyof SubTask, value: any) => {
    setSubTasks(subTasks.map(subTask => 
      subTask.id === id ? { ...subTask, [field]: value } : subTask
    ));
  };

  // 하위 과제 삭제
  const deleteSubTask = (id: string) => {
    setSubTasks(subTasks.filter(subTask => subTask.id !== id));
  };

  // 드래그 앤 드롭 핸들러
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setSubTasks((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);

        const reorderedItems = arrayMove(items, oldIndex, newIndex);
        // 순서를 1부터 시작하도록 다시 설정
        return reorderedItems.map((item, index) => ({ ...item, order: index + 1 }));
      });
    }
  };

  // 멤버를 세로 정렬로 그룹화하는 함수
  const grouped = useMemo(() => {
    // 동적 컬럼 수에 맞춰 멤버들을 균등하게 분배
    const columnCount = currentGroupMembers.length <= 25 ? Math.ceil(currentGroupMembers.length / 5) : Math.ceil(currentGroupMembers.length / 6);
    const perCol = Math.ceil(currentGroupMembers.length / columnCount);
    const columns: ClassMember[][] = [];
    currentGroupMembers.forEach((m, idx) => {
      const colIdx = Math.floor(idx / perCol);
      if (!columns[colIdx]) columns[colIdx] = [];
      columns[colIdx].push(m);
    });
    return columns;
  }, [currentGroupMembers]);

  // 동적 컬럼 수 계산 - 멤버 수에 따라 더 적절한 열 수 계산
  const columnCount = currentGroupMembers.length <= 25 ? Math.ceil(currentGroupMembers.length / 5) : Math.ceil(currentGroupMembers.length / 6);

  const handleProjectAssignmentSave = async () => {
    if (!title.trim()) {
      alert('과제 이름을 입력해주세요.');
      return;
    }

    if (subTasks.length === 0) {
      alert('최소 하나의 하위 과제를 추가해주세요.');
      return;
    }

    if (subTasks.some(st => !st.title.trim())) {
      alert('모든 하위 과제의 제목을 입력해주세요.');
      return;
    }

    if (!dueDate) {
      alert('마감일을 선택해주세요.');
      return;
    }

    const dueDateObj = new Date(dueDate);
    if (isNaN(dueDateObj.getTime())) {
      alert('올바른 마감일을 선택해주세요.');
      return;
    }

    const groupsToApply = normalizedGroups.filter(g => (selectedByGroup[g]?.size || 0) > 0);
    if (groupsToApply.length === 0) {
      alert('최소 한 명의 멤버를 선택해주세요.');
      return;
    }

    // 새로 생성하거나 복사할 때만 과제 타입 제한 체크
    if ((mode === 'new' || mode === 'copy') && !editingProject?.id) {
      const limitCheck = checkAssignmentTypeLimit(user, 'project');
      if (!limitCheck.allowed) {
        setLicenseLimitMessage(limitCheck.message || '');
        setShowLicenseLimitModal(true);
        return;
      }
    }

    try {
      const baseFormData: any = {
        title: title.trim(),
        description: description.trim(),
        dueDate: dueDateObj,
        subTasks: subTasks.map((st, index) => ({ ...st, order: index + 1 })),
        color: selectedColor
      };

      // 프로젝트 보상 필드 추가 - rewardEnabled는 항상 저장 (false도 저장)
      // 코인, 보상 카드, 완료 메시지 중 하나라도 있어야 true 유지
      // 모두 없으면 false로 저장
      const hasRewards = (getRewardCoinsNum() > 0) || (rewardItemCardIds.length > 0);
      const hasCompletionMessage = rewardMessage && rewardMessage.trim().length > 0;
      const shouldEnableReward = rewardEnabled && (hasRewards || hasCompletionMessage);
      
      baseFormData.rewardEnabled = shouldEnableReward;
      
      if (shouldEnableReward) {
        if (rewardMessage && rewardMessage.trim()) {
          baseFormData.rewardMessage = rewardMessage;
        }
        // 코인이 0이어도 명시적으로 전달 (서비스에서 삭제 처리)
        baseFormData.rewardCoins = getRewardCoinsNum();
        // 카드가 0개여도 명시적으로 전달 (서비스에서 삭제 처리)
        baseFormData.rewardItemCardIds = rewardItemCardIds;
        console.log('🔍 ProjectAssignmentModal - 프로젝트 보상 필드 추가:', {
          rewardEnabled: shouldEnableReward,
          rewardMessage,
          rewardCoins: getRewardCoinsNum(),
          rewardItemCardIds
        });
      } else {
        console.log('🔍 ProjectAssignmentModal - 프로젝트 보상 비활성화됨:', {
          rewardEnabled: rewardEnabled,
          hasRewards: hasRewards,
          hasCompletionMessage: hasCompletionMessage
        });
      }

      console.log('💾 프로젝트 과제 저장 시도:', { groupsToApply, baseFormData });

      if ((mode === 'edit' && initialData?.id) || editingProject?.id) {
        const projectId = editingProject?.id || initialData?.id;
        for (const g of groupsToApply) {
          const formData: any = { ...baseFormData, assignedStudentIds: Array.from(selectedByGroup[g] || new Set()) };
          const groupProjects = await projectAssignmentService.getProjectAssignments(userId, currentClassId, g);
          const existsInGroup = groupProjects.some(p => p.id === projectId);
          if (existsInGroup) {
            await projectAssignmentService.updateProjectAssignment(userId, currentClassId, projectId!, formData, g);
          } else {
            await projectAssignmentService.createProjectAssignment(userId, currentClassId, formData, g, projectId!);
          }
        }
        console.log('✅ 프로젝트 과제 수정 완료 (그룹별, 없던 그룹에는 생성)');
      } else {
        const sharedId = generateAssignmentId('project');
        for (const g of groupsToApply) {
          const formData: any = { ...baseFormData, assignedStudentIds: Array.from(selectedByGroup[g] || new Set()) };
          await projectAssignmentService.createProjectAssignment(userId, currentClassId, formData, g, sharedId);
        }
        console.log('✅ 프로젝트 과제 생성 완료 (그룹별), ID:', sharedId);
      }

      // 성공 시 콜백 호출
      onSave(baseFormData);
      onClose();
    } catch (error) {
      console.error('❌ 프로젝트 과제 저장 실패:', error);
      alert(`프로젝트 과제 저장에 실패했습니다: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <ModalOverlay onClick={handleClose}>
      <ModalContent
        $isRestricted={user?.userType === 'free' || user?.userType === 'basic'}
        onClick={(e) => e.stopPropagation()}
      >
        <Header>
          <Title>
            {editingProject && editingProject.id ? '프로젝트 과제 수정' :
             editingProject && editingProject.title?.includes('(복사)') ? '복제 과제 만들기' :
             '프로젝트 과제 생성'}
          </Title>
          {(user?.userType === 'free' || user?.userType === 'basic') && (
            <RestrictionNotice>
              {user?.userType === 'free'
                ? '프리 플랜에서는 프로젝트 과제를 사용할 수 없습니다. 이용권을 업그레이드하세요.'
                : '베이직 플랜에서는 프로젝트 과제를 사용할 수 없습니다. 이용권을 업그레이드하세요.'
              }
            </RestrictionNotice>
          )}
          <CloseButton onClick={handleClose}>×</CloseButton>
        </Header>

        <Content>
          {/* 섹션 1: 과제 이름, 과제 설명 */}
          <FormSection>
            <FieldRow>
              <Field>
                <Label>과제 이름</Label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="과제 이름을 입력하세요"
                  tabIndex={1}
                />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field>
                <Label>과제 설명 (선택사항)</Label>
                <TextArea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="과제에 대한 추가 설명이나 메모를 입력하세요..."
                  rows={3}
                  tabIndex={2}
                />
              </Field>
            </FieldRow>
          </FormSection>

          {/* 섹션 2: 프로젝트 완료 보상 */}
          <FormSection>
            {/* 프로젝트 완료 보상 토글 */}
            <Field>
              <ToggleRow>
                <Label>프로젝트 완료 보상</Label>
                <ToggleSwitch 
                  $isOn={rewardEnabled}
                  onClick={() => setRewardEnabled(!rewardEnabled)}
                >
                  <ToggleSlider $isOn={rewardEnabled}>
                    {rewardEnabled ? 'ON' : 'OFF'}
                  </ToggleSlider>
                </ToggleSwitch>
              </ToggleRow>
            </Field>

            {/* 프로젝트 보상 섹션 */}
            {rewardEnabled && (
              <>
              {/* 프로젝트 완료 메시지 */}
              <Field>
                <Label>프로젝트 완료 메시지</Label>
                <NotesTextarea
                  value={rewardMessage}
                  onChange={(e) => setRewardMessage(e.target.value)}
                  placeholder="프로젝트 완료 시 멤버에게 표시할 안내 메시지를 입력하세요..."
                  rows={2}
                />
              </Field>

              {/* 획득 코인 */}
              <Field>
                <LabelRow>
                  <Label>획득 코인</Label>
                  <IncButtons>
                    <ResetBtn type="button" aria-label="코인 초기화" onClick={() => setRewardCoinsDisplay('')}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                    </ResetBtn>
                    <RoundBtn type="button" aria-label="코인 빼기" onClick={() => {
                      const n = getRewardCoinsNum();
                      if (n > 0) setRewardCoinsDisplay(String(Math.max(0, n - 10)));
                    }}>
                      <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </RoundBtn>
                    <RoundBtn type="button" aria-label="코인 더하기" onClick={() => {
                      setRewardCoinsDisplay(String(getRewardCoinsNum() + 10));
                    }}>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </RoundBtn>
                  </IncButtons>
                </LabelRow>
                <Input
                  type="number"
                  min={0}
                  value={rewardCoinsDisplay}
                  onChange={(e) => setRewardCoinsDisplay(e.target.value)}
                  onBlur={() => { if (rewardCoinsDisplay === '') setRewardCoinsDisplay('0'); }}
                />
              </Field>

              {/* 획득 카드 설정 */}
              <Field>
                <Label>획득 카드 설정 (프로젝트보상 아이템)</Label>
                <SelectedRewardsContainer>
                  {selectedItemCards.map((itemCard, index) => (
                      <SelectedRewardCard key={itemCard.itemCardId}>
                        <RewardCardIconArea>
                          {itemCard.cardImage === 'default' || rewardCardImageErrors.has(itemCard.itemCardId) ? (
                            <RewardCardPlaceholderIcon>
                              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="2" y="6" width="12" height="16" rx="2" fill="#ffffff" stroke="#555" strokeWidth="1.5" transform="rotate(-10 8 14)"/>
                                <rect x="6" y="4" width="12" height="16" rx="2" fill="#f8f8f8" stroke="#555" strokeWidth="1.5" transform="rotate(0 12 12)"/>
                                <rect x="10" y="6" width="12" height="16" rx="2" fill="#fafafa" stroke="#555" strokeWidth="1.5" transform="rotate(10 16 14)"/>
                              </svg>
                            </RewardCardPlaceholderIcon>
                          ) : itemCard.cardImage && /^\d+$/.test(itemCard.cardImage) ? (
                            <img src={`/CardIcon/IconBasic/IconBasic-${itemCard.cardImage}.png`} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                          ) : itemCard.cardImage ? (
                            <img src={itemCard.cardImage} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                          ) : itemCard.icon ? (
                            <img src={itemCard.icon} alt={itemCard.name} onError={() => setRewardCardImageErrors(prev => new Set(prev).add(itemCard.itemCardId))} />
                          ) : (
                            <RewardCardPlaceholderIcon>
                              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="8" fill="var(--color-surface-highlight)"/>
                                <path d="M24 14V34M14 24H34" stroke="#999" strokeWidth="3" strokeLinecap="round"/>
                              </svg>
                            </RewardCardPlaceholderIcon>
                          )}
                        </RewardCardIconArea>
                        <RewardCardName>{itemCard.name}</RewardCardName>
                        <RewardCardMemo>{itemCard.memo}</RewardCardMemo>
                        <RemoveRewardButton
                          onClick={(e) => {
                            e.stopPropagation();
                            const newRewards = rewardItemCardIds.filter(id => id !== itemCard.itemCardId);
                            setRewardItemCardIds(newRewards);
                            loadSelectedItemCards(newRewards);
                          }}
                        >
                          ×
                        </RemoveRewardButton>
                      </SelectedRewardCard>
                    ))}
                    <AddRewardButton
                      disabled={user?.userType === 'free' || user?.userType === 'basic'}
                      onClick={() => {
                        if (user?.userType === 'free' || user?.userType === 'basic') {
                          setLicenseLimitMessage('프리/베이직 플랜에서는 아이템 카드를 과제 보상으로 사용할 수 없습니다. 이용권을 업그레이드하세요.');
                          setShowLicenseLimitModal(true);
                          return;
                        }
                        setIsItemCardModalOpen(true);
                      }}
                      type="button"
                    >
                      <AddRewardIcon>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </AddRewardIcon>
                      <AddRewardText>아이템 카드 추가</AddRewardText>
                    </AddRewardButton>
                </SelectedRewardsContainer>
              </Field>
            </>
            )}
          </FormSection>

          {/* 섹션 3: 마감 날짜, 하위 과제 설정, 프로젝트 색상 */}
          <FormSection>
            <FieldRow>
            <Field>
              <LabelRow>
                <Label>마감 날짜</Label>
                <IncButtons>
                  <ResetBtn type="button" aria-label="날짜 초기화" onClick={() => {
                    const today = new Date();
                    const yyyy = today.getFullYear();
                    const mm = String(today.getMonth() + 1).padStart(2, '0');
                    const dd = String(today.getDate()).padStart(2, '0');
                    const todayStr = `${yyyy}-${mm}-${dd}`;
                    setDueDate(todayStr);
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="2" y="2" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </ResetBtn>
                  <RoundBtn type="button" aria-label="하루 빼기" onClick={() => {
                    if (!dueDate) return; const d = new Date(dueDate); d.setDate(d.getDate() - 1); setDueDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 1H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                  <RoundBtn type="button" aria-label="하루 더하기" onClick={() => {
                    if (!dueDate) return; const d = new Date(dueDate); d.setDate(d.getDate() + 1); setDueDate(d.toISOString().slice(0,10));
                  }}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RoundBtn>
                </IncButtons>
              </LabelRow>
              <DateInputWrapper>
                {dueDate && (
                  <WeekdayBadge>
                    {['일','월','화','수','목','금','토'][new Date(dueDate).getDay()]}
                    {(() => {
                      const today = new Date();
                      const due = new Date(dueDate);
                      
                      // 날짜만 비교하기 위해 시간 정보 제거
                      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      const dueDateOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
                      
                      const diffTime = dueDateOnly.getTime() - todayDate.getTime();
                      const diffDays = diffTime / (1000 * 60 * 60 * 24);
                      
                      if (diffDays === 0) return ' (오늘)';
                      if (diffDays === 1) return ' (+1)';
                      if (diffDays === 2) return ' (+2)';
                      if (diffDays === 3) return ' (+3)';
                      if (diffDays === 4) return ' (+4)';
                      if (diffDays === 5) return ' (+5)';
                      if (diffDays === 6) return ' (+6)';
                      if (diffDays === 7) return ' (+7)';
                      if (diffDays > 7) return ` (+${diffDays})`;
                      if (diffDays === -1) return ' (-1)';
                      if (diffDays === -2) return ' (-2)';
                      if (diffDays === -3) return ' (-3)';
                      if (diffDays < -3) return ` (${diffDays})`;
                      return '';
                    })()}
                  </WeekdayBadge>
                )}
                <DateInput
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  tabIndex={3}
                />
              </DateInputWrapper>
            </Field>
          </FieldRow>

          <Field>
            <Label>하위 과제 설정</Label>
            <SubTasksContainer>
              <SubTasksTable>
                <thead>
                  <tr>
                    <SubTaskTableHeader $width="40px">순</SubTaskTableHeader>
                    <SubTaskTableHeader $width="2fr">하위 과제 제목</SubTaskTableHeader>
                    <SubTaskTableHeader $width="0.5fr">포인트</SubTaskTableHeader>
                    <SubTaskTableHeader $width="60px">삭제</SubTaskTableHeader>
                    <SubTaskTableHeader $width="60px">이동</SubTaskTableHeader>
                  </tr>
                </thead>
              </SubTasksTable>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={subTasks.map(st => st.id)} strategy={verticalListSortingStrategy}>
                  <SubTasksList>
                    {subTasks.map((subTask, index) => (
                      <SortableSubTaskRow
                        key={subTask.id}
                        subTask={subTask}
                        updateSubTask={updateSubTask}
                        removeSubTask={deleteSubTask}
                        order={index + 1}
                      />
                    ))}
                  </SubTasksList>
                </SortableContext>
              </DndContext>
              
              <AddSubTaskRow>
                <AddSubTaskButton type="button" onClick={addSubTask}>
                  +
                </AddSubTaskButton>
              </AddSubTaskRow>
            </SubTasksContainer>
          </Field>

          <Field>
            <Label>프로젝트 색상</Label>
            <CategoryDisplay>
              <ColorPickerButton onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}>
                <ColorDot color={selectedColor} />
                <span>프로젝트</span>
                <ColorPickerIcon>▼</ColorPickerIcon>
              </ColorPickerButton>
              
              {isColorPickerOpen && (
                <>
                  <ColorPickerOverlay onClick={() => setIsColorPickerOpen(false)} />
                  <ColorPickerDropdown>
                    <ColorGrid>
                      {projectColors.map((color) => (
                        <ColorOption
                          key={color}
                          color={color}
                          selected={selectedColor === color}
                          onClick={() => {
                            setSelectedColor(color);
                            setIsColorPickerOpen(false);
                          }}
                        />
                      ))}
                    </ColorGrid>
                  </ColorPickerDropdown>
                </>
              )}
            </CategoryDisplay>
          </Field>
          </FormSection>

          {/* 섹션 4: 과제를 부여할 멤버 선택 */}
          <FormSection>
            <SelectHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>과제를 부여할 멤버 선택</span>
                {hoveredStudent && (
                  <SelectedStudentBadge key={hoveredStudent.id + Date.now()}>
                    {hoveredStudent.name}
                  </SelectedStudentBadge>
                )}
              </div>
              <SelectAllButton onClick={toggleAll}>{selected.size === currentGroupMembers.length ? '전체 해제' : '전체 선택'}</SelectAllButton>
            </SelectHeader>

            {normalizedGroups.length > 1 && (
              <GroupControls>
                <GroupTabs>
                  {normalizedGroups.map((g) => (
                    <GroupTabButton
                      key={g}
                      type="button"
                      $active={activeGroup === g}
                      onClick={async () => {
                        setActiveGroup(g);
                        await ensureMembersLoaded(g);
                        await ensureAssignmentSelectionLoaded(g);
                      }}
                    >
                      {getGroupDisplayName(g)}
                    </GroupTabButton>
                  ))}
                </GroupTabs>
              </GroupControls>
            )}

            <StudentsGrid $columnCount={columnCount}>
                {grouped.map((col, colIdx) => (
                  <Column key={colIdx}>
                    {col.map(m => (
                      <div key={m.id} style={{ position: 'relative' }}>
                        <StudentButton
                          onClick={() => {
                            toggle(m.id);
                            setHoveredStudent(hoveredStudent?.id === m.id ? null : m);
                          }}
                          aria-pressed={selected.has(m.id)}
                          title={m.name}
                        >
                          <span className="num">{String(m.number).padStart(2, '0')}</span>
                          <span className="name">{m.name}</span>
                        </StudentButton>
                      </div>
                    ))}
                  </Column>
                ))}
            </StudentsGrid>
          </FormSection>
        </Content>

        <Buttons>
          <CancelButton data-variant="modal-secondary" onClick={handleClose}>취소</CancelButton>
          <SaveButton 
            onClick={handleProjectAssignmentSave} 
            disabled={!title.trim() || subTasks.length === 0 || normalizedGroups.every(g => (selectedByGroup[g]?.size || 0) === 0) || subTasks.some(task => !task.title.trim())}
          >
            저장
          </SaveButton>
        </Buttons>
      </ModalContent>
    </ModalOverlay>

    {/* 아이템 카드 선택 모달 */}
    <ItemCardSelectionModal
      isOpen={isItemCardModalOpen}
      onClose={() => setIsItemCardModalOpen(false)}
      onConfirm={(selectedItemCardIds) => {
        setRewardItemCardIds(selectedItemCardIds);
        loadSelectedItemCards(selectedItemCardIds);
        setIsItemCardModalOpen(false);
      }}
      userId={userId}
      classId={currentClassId}
      initialSelectedIds={rewardItemCardIds}
      categoryFilter={['assignment']}
    />
    
    <LicenseLimitModal
      isOpen={showLicenseLimitModal}
      message={licenseLimitMessage}
      onClose={() => setShowLicenseLimitModal(false)}
      onUpgrade={() => {
        setShowLicenseLimitModal(false);
        onLicenseClick?.();
      }}
    />

    {/* 변경사항 확인 모달 */}
    <ConfirmModal
      isOpen={showConfirmModal}
      onClose={() => setShowConfirmModal(false)}
      onConfirm={() => {
        setShowConfirmModal(false);
        onClose();
      }}
      title={['변경된 데이터가 있습니다.', '팝업을 닫으시겠습니까?']}
      confirmText="닫기"
      cancelText="취소"
      icon="!"
      iconBackground="var(--status-pending)"
    />

  </>
  );
};

export default ProjectAssignmentSettingsModal;

// 스타일 컴포넌트들 (그룹 탭 UI - 컴포넌트 외부에 정의하여 동적 생성 경고 방지)
const GroupControls = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin: 10px 0 6px;
  flex-wrap: wrap;
`;

const GroupTabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const GroupTabButton = styled.button<{ $active?: boolean }>`
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: ${p => (p.$active ? 'var(--color-accent-strong)' : 'var(--color-panel)')};
  color: ${p => (p.$active ? 'white' : 'var(--color-text-primary)')};
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    padding: 10px;
    padding-top: 20px;
  }
`;

const ModalContent = styled.div<{ $isRestricted?: boolean }>`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 30px 20px;
  width: 100%;
  max-width: 800px;
  box-shadow: var(--modal-shadow);
  border: var(--modal-border);
  position: relative;
  margin: 150px auto;
  ${props => props.$isRestricted && `
    filter: grayscale(100%);
    pointer-events: none;
    user-select: none;
  `}

  @media (max-width: 768px) {
    padding: 20px 16px;
    margin: 150px auto 20px auto;
    width: calc(100% - 20px);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  margin: 0 0 16px;
  text-align: center;
  font-size: 1.2rem;
  color: var(--color-text-primary);
  flex: 1;
`;

const RestrictionNotice = styled.div`
  text-align: center;
  padding: 12px 16px;
  margin: -8px 0 16px 0;
  background: var(--color-accent-soft);
  border: 1px solid var(--color-accent);
  border-radius: 6px;
  color: var(--color-accent-text);
  font-size: 0.9rem;
  font-weight: 500;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--color-text-gray);
  
  &:hover {
    color: var(--color-text-primary);
  }
`;

const Content = styled.div`
  /* 내용 영역 */
`;

const FormSection = styled.div`
  background: var(--color-section-bg);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid var(--color-section-border);
  
  @media (max-width: 768px) {
    padding: 16px;
    margin-bottom: 16px;
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 10px;
  
  ${FormSection} &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  min-width: 0;
  height: 38px;
  box-sizing: border-box;
  text-align: center;
  width: 100%;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const TextArea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  width: 100%;
  text-align: center;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const NotesTextarea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  width: 100%;
  text-align: center;
  min-height: 60px;
  background: var(--color-surface);
  color: var(--color-text-primary);
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  &::placeholder {
    color: var(--color-text-muted);
    text-align: center;
  }
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const DateInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 8px 10px;
  background: var(--color-surface);
  height: 38px;
  box-sizing: border-box;
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const DateInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  flex: 1;
  min-width: 0;
  color: var(--color-text-primary);
  
  /* 브라우저 기본 달력 아이콘 스타일링 - 다크 모드에서만 흰색으로 */
  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 1;
    filter: var(--date-calendar-icon-filter);
  }
  
  &::-moz-calendar-picker-indicator {
    cursor: pointer;
    opacity: 1;
    filter: var(--date-calendar-icon-filter);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const IncButtons = styled.div`
  display: flex;
  gap: 2px;
  flex-shrink: 0;
`;

const ResetBtn = styled.button`
  padding: 4px 8px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    color: var(--color-text-primary);
    
    rect,
    path {
      stroke: var(--color-text-primary);
    }
  }
`;

const RoundBtn = styled.button`
  padding: 4px 8px;
  height: 26px;
  min-height: 26px;
  background: var(--color-surface)fff;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: var(--color-border);
    border-color: var(--color-text-muted);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  svg {
    color: var(--color-text-primary);
    
    rect,
    path {
      stroke: var(--color-text-primary);
    }
  }
`;

const WeekdayBadge = styled.span`
  background-color: var(--weekday-badge-bg);
  color: var(--weekday-badge-text-color);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: var(--weekday-badge-text-weight);
  white-space: nowrap;
  flex-shrink: 0;
`;

const SubTasksContainer = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  overflow: hidden;
`;

const SubTasksTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  display: grid;
  grid-template-columns: 40px minmax(150px, 1fr) 60px 60px 60px;
  
  thead, tbody, tr {
    display: contents;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 35px minmax(100px, 1fr) 50px 50px 50px;
    min-width: 0;
  }
`;

const SubTaskTableHeader = styled.th<{ $width?: string }>`
  background: var(--color-surface-highlight);
  color: var(--color-text-primary);
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  border-bottom: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: 8px 4px;
    font-size: 0.75rem;
  }
`;

const SubTasksList = styled.div`
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
`;

const SubTaskRow = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(150px, 1fr) 60px 60px 60px;
  border-bottom: 1px solid var(--color-border);
  width: 100%;
  background: var(--color-surface);
  
  @media (max-width: 768px) {
    grid-template-columns: 35px minmax(100px, 1fr) 50px 50px 50px;
  }
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }
`;

const SubTaskCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid var(--color-border);
  transition: background-color 0.2s ease;
  min-width: 0;
  overflow: hidden;

  &:hover {
    background-color: var(--color-panel);
  }

  &:last-child {
    border-right: none;
  }
  
  &:nth-child(2) { /* 하위 과제 제목 셀 */
    justify-content: flex-start;
  }
  
  @media (max-width: 768px) {
    padding: 8px 4px;
    
    &:nth-child(1) { /* 순 */
      min-width: 35px;
    }
    &:nth-child(2) { /* 하위 과제 제목 */
      min-width: 0;
      flex: 1;
      justify-content: flex-start;
    }
    &:nth-child(3) { /* 포인트 */
      min-width: 50px;
    }
    &:nth-child(4), &:nth-child(5) { /* 삭제, 이동 */
      min-width: 50px;
    }
  }
`;

const SubTaskInput = styled.input`
  padding: 6px 8px;
  border: none;
  border-right: 1px solid var(--color-border);
  outline: none;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  width: 100%;
  white-space: nowrap;
  background: var(--color-surface);
  color: var(--color-text-primary);

  &:focus {
    outline: none;
  }
  
  &:focus-visible {
    outline: none;
  }
  
  &[type="text"] {
    text-align: left;
  }
  
  &[type="number"] {
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: 4px 6px;
    font-size: 0.75rem;
    
    &[type="text"] {
      text-align: left;
    }
    
    &[type="number"] {
      text-align: center;
    }
  }
`;

const DeleteSubTaskButton = styled.button`
  background: transparent;
  color: #6c757d;
  border: none;
  cursor: pointer;
  padding: 4px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    color: var(--color-text-secondary);
  }

  &:focus {
    outline: none;
    background: var(--color-panel);
    color: var(--color-text-secondary);
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  color: #6c757d;
  min-width: 24px;
  min-height: 24px;
  transition: color 0.2s ease;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    color: var(--color-text-secondary);
  }
`;

const AddSubTaskRow = styled.div`
  padding: 12px;
  text-align: center;
  background: var(--color-panel);
`;

const AddSubTaskButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(var(--color-accent-rgb), 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: var(--color-accent);
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(var(--color-accent-rgb), 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const CategoryDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
`;

const ColorPickerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--color-panel);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  color: var(--color-text-secondary);
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: #e0e0e0;
    border-color: var(--color-border);
  }

  /* 다크 모드: 배경보다 조금 밝게 */
  :root:not([data-theme='light']) & {
    &:hover {
      background: color-mix(in srgb, var(--color-panel) 88%, white);
      border-color: color-mix(in srgb, var(--color-panel) 88%, white);
    }
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ColorPickerIcon = styled.span`
  font-size: 10px;
  color: #6c757d;
  transition: transform 0.2s ease;
`;

const ColorPickerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10001;
  background: transparent;
`;

const ColorPickerDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  box-shadow: var(--modal-shadow);
  z-index: 10002;
  padding: 12px;
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
`;

const ColorOption = styled.button<{ color: string; selected: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid ${props => props.selected ? '#000' : 'transparent'};
  background-color: ${props => props.color};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  ${props => props.selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 14px;
      font-weight: bold;
      text-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    }
  `}
`;

const ColorDot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  background-color: ${props => props.color};
  border-radius: 50%;
  flex-shrink: 0;
`;

const SelectHeader = styled.div`
  display: flex; 
  align-items: center; 
  justify-content: space-between; 
  margin: 8px 0;
`;

const SelectAllButton = styled.button`
  padding: 6px 10px; 
  background: var(--color-surface); 
  border: 2px solid var(--color-border); 
  border-radius: 8px; 
  font-weight: 600; 
  cursor: pointer;
  color: var(--color-text-primary);
  &:focus-visible { 
    outline: 2px solid #000; 
    outline-offset: 2px; 
  }
`;

const StudentsGrid = styled.div<{ $columnCount: number }>`
  display: grid; 
  grid-template-columns: repeat(${props => props.$columnCount}, auto);
  gap: 6px; 
  align-items: start; 
  justify-items: center;
  justify-content: center;
  padding: 8px; 
  border: 2px solid var(--color-border); 
  border-radius: 8px; 
  background: var(--color-panel);
  width: 100%;
  max-height: 300px;
  overflow-x: auto;
  overflow-y: auto;
  margin: 0 auto;
`;

const Column = styled.div`
  display: grid; 
  grid-auto-rows: minmax(36px, auto); 
  gap: 6px;
`;

const StudentButton = styled.button<{ disabled?: boolean }>`
  display: flex; 
  align-items: center; 
  gap: 4px; 
  padding: 6px 8px; 
  background: var(--color-surface); 
  border: 1px solid var(--color-border); 
  border-radius: 8px; 
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  width: 100%;
  min-height: 36px;
  box-sizing: border-box;
  transition: all 0.2s ease;
  box-shadow: none;
  
  .num { 
    font-weight: 700; 
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-border)'};
    font-size: 16px;
    flex-shrink: 0;
  }
  .name { 
    display: inline; 
    color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-text-muted)'};
    font-weight: 500;
    font-size: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;
  }
  
  @media (max-width: 600px) { 
    .name { 
      font-size: 14px;
    }
  }
  
  @media (max-width: 520px) { 
    justify-content: center;
    .name { display: none; } 
  }
  
  /* 선택된 상태 */
  &[aria-pressed="true"] {
    background: var(--color-surface);
    border-color: var(--color-accent-text);
    border-width: 1px;
    box-shadow: none;

    .num {
      color: var(--color-accent-text);
      font-weight: 700;
    }
    .name {
      color: var(--color-text-primary);
      font-weight: 600;
    }
  }
  
  /* 호버 효과 */
  &:hover {
    background-color: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-border)'};
  }
  
  /* 선택된 상태에서 호버 시 테두리 유지 */
  &[aria-pressed="true"]:hover {
    border-color: var(--color-accent-text);
    border-width: 1px;
    box-shadow: none;
  }
  
  &:focus-visible { 
    outline: 2px solid #000; 
    outline-offset: 2px; 
  }
`;

const SelectedStudentBadge = styled.span`
  background-color: var(--color-student-badge-bg);
  color: var(--color-student-badge-text);
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-left: 8px;
  animation: slideInFromRight 0.3s ease-out, fadeOut 0.5s ease-in 1.5s forwards;
  
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: translateX(0);
    }
    to {
      opacity: 0;
      transform: translateX(0);
    }
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-save-button-disabled-bg)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

// 프로젝트 보상 토글 스타일
const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ToggleSwitch = styled.button<{ $isOn: boolean }>`
  width: 70px;
  height: 28px;
  min-width: 70px;
  min-height: 28px;
  border-radius: 14px;
  background: ${props => props.$isOn ? 'var(--color-toggle-on-bg)' : 'var(--color-toggle-off-bg)'};
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.3s ease;
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ToggleSlider = styled.div<{ $isOn: boolean }>`
  width: 34px;
  height: 22px;
  border-radius: 11px;
  background: var(--color-surface);
  position: absolute;
  top: 3px;
  left: ${props => props.$isOn ? '34px' : '3px'};
  transition: left 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: ${props => props.$isOn ? 'var(--color-toggle-on-text)' : 'var(--color-toggle-off-text)'};
`;

const SelectedRewardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, 120px);
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-surface);
  min-height: 220px;
  justify-content: center;
  
  :root[data-theme='light'] & {
    border-color: var(--color-section-border);
  }
`;

const RemoveRewardButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--color-accent-strong);
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  z-index: 1;

  /* 다크 모드: 흰색 배경에 회색 X */
  :root:not([data-theme='light']) & {
    background: rgba(255, 255, 255, 1);
    color: #333333;
  }

  &:hover {
    background: rgba(var(--color-accent-rgb), 0.8);
    transform: scale(1.1);
  }

  /* 다크 모드: 호버 시에도 흰색 유지 */
  :root:not([data-theme='light']) &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
  z-index: 1;

  &:hover {
    background: var(--color-accent);
    transform: scale(1.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;


const AddRewardButton = styled.button<{ disabled?: boolean }>`
  width: 120px;
  height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10%;
  background: var(--color-surface);
  border: 2px dashed ${props => props.disabled ? 'var(--color-border)' : 'var(--color-accent-strong)'};
  border-radius: 8px;
  color: ${props => props.disabled ? 'var(--color-text-muted)' : 'var(--color-accent-text)'};
  font-size: 12px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-sizing: border-box;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    background: ${props => props.disabled ? 'var(--color-surface)' : 'var(--color-accent-soft)'};
    border-color: ${props => props.disabled ? 'var(--color-border)' : 'rgba(var(--color-accent-rgb), 0.8)'};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AddRewardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 2px dashed var(--color-accent-strong);
  border-radius: 50%;
  background: var(--color-accent-soft);
`;

const AddRewardText = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent-text);
  text-align: center;
  margin-top: 8px;
`;

const SelectedRewardCard = styled.div`
  width: 120px;
  height: 200px;
  border: 2px solid var(--color-accent-strong);
  border-radius: 8px;
  background: var(--color-surface);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10%;
  position: relative;
  box-sizing: border-box;
  transition: all 0.2s ease;

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RewardCardIconArea = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: var(--color-panel);
  border: 2px solid var(--color-accent-strong);

  /* 다크 모드: 흰색 테두리 */
  :root:not([data-theme='light']) & {
    border: 2px solid rgba(255, 255, 255, 0.8);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RewardCardPlaceholderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RewardCardName = styled.div`
  width: 100%;
  margin-top: 8%;
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  text-align: center;
  line-height: 1.3;
  flex-shrink: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RewardCardMemo = styled.div`
  width: 100%;
  margin-top: 4%;
  margin-bottom: auto;
  font-size: 8px;
  color: #999;
  text-align: center;
  word-break: break-word;
  line-height: 1.3;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 0;
`;
